import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { randomNumber } from '../../lib/constants';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addTransportType'
  | 'bulkAddTransportTypes'
  | 'getTransportType'
  | 'getTransportTypes'
  | 'updateTransportType';

export const useTransportTypes = () => {
  const { error, loading, makeRequest, response } = useFetch();
  const {
    id: operatorId,
    type: operatorType,
    transports,
    updateOperatorState,
  } = useOperator();
  const { sortAsc } = useSort<TransportType>('name');
  const [action, setAction] = useState<Action>('');
  const [bulkTransportTypes, setBulkTransportTypes] =
    useState<BulkTransportTypes>();
  const [transportType, setTransportType] = useState<TransportType | null>(
    null
  );
  const [transportTypes, setTransportTypes] = useState<TransportType[]>([]);
  const BASE_URL =
    operatorType === 'Residential'
      ? `${BASE_URL_MAP['Residential']}/${operatorId}/TransportTypes`
      : `${BASE_URL_MAP['Hospitality']}/TransportTypes`;

  useEffect(() => {
    if (response && !loading) {
      if (action === 'bulkAddTransportTypes') {
        const bulkResponse = mapBulkResponse(response, operatorType);
        setBulkTransportTypes(bulkResponse);
        updateOperatorState('transports', [
          ...(transports ?? []).concat(bulkResponse.success).sort(sortAsc),
        ]);
      } else if (action === 'updateTransportType') {
        const mappedTransportType = mapTransportType(response, operatorType);
        setTransportType(mappedTransportType);
        updateOperatorState(
          'transports',
          [
            mappedTransportType,
            ...(transports ?? []).filter((transportType) => {
              if (operatorType === 'Residential')
                return transportType.name !== mappedTransportType.name;
              else return transportType.id !== mappedTransportType.id;
            }),
          ].sort(sortAsc)
        );
      } else if (action === 'addTransportType') {
        const mappedDeviceModel = mapTransportType(response, operatorType);
        updateOperatorState(
          'transports',
          [mappedDeviceModel, ...(transports ?? [])].sort(sortAsc)
        );
      } else if (action === 'getTransportTypes')
        setBulkTransportTypes(
          response.map((transportType: ApiResTransportType) =>
            mapTransportType(transportType, operatorType)
          )
        );
    }
  }, [loading, response]);

  useEffect(() => {}, [error, loading]);

  useEffect(() => {
    resetState();
  }, [operatorId]);

  const addTransportType = (transportType: CreateTransportType) => {
    validateTransportType(transportType);
    setAction('addTransportType');
    const body: ApiResTransportType = {
      Active: transportType.active,
      Name: transportType.name,
      IngressHD: transportType.ingressHD,
      IngressSD: transportType.ingressSD,
      WanHD: transportType.wanHD,
      WanSD: transportType.wanSD,
    };
    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const bulkAddTransportTypes = (transportType: CreateTransportType[]) => {
    if (operatorType === 'Hospitality')
      throw 'Bulk Device Models is only available for Residential Operators.';
    setAction('bulkAddTransportTypes');
    setBulkTransportTypes({ success: [], fail: [] });
    const body = transportType.map((transportType) => ({
      Active: transportType.active,
      Name: transportType.name,
      IngressHD: transportType.ingressHD,
      IngressSD: transportType.ingressSD,
      WanHD: transportType.wanHD,
      WanSD: transportType.wanSD,
    }));

    makeRequest(`${BASE_URL}/BulkAdd`, 'post', sanitizeValue(body));
  };

  const clearBulkAdds = () => setBulkTransportTypes({ success: [], fail: [] });

  const getTransportTypes = () => {
    setAction('getTransportTypes');
    makeRequest(BASE_URL, 'get');
  };

  const getTransportType = (deviceModelId: string) => {
    setAction('getTransportType');
    makeRequest(`${BASE_URL}/${deviceModelId}`, 'get');
  };

  const updateTransportType = (transportType: TransportType) => {
    validateTransportType(transportType);
    setAction('updateTransportType');
    const transportTypeId =
      operatorType === 'Residential' ? transportType.name : transportType.id;
    const body = mapApiTransportType(transportType, operatorType);

    makeRequest(`${BASE_URL}/${transportTypeId}`, 'put', body);
  };

  const resetState = () => {
    setAction('');
    setBulkTransportTypes({ success: [], fail: [] });
    setTransportTypes([]);
    setTransportType(null);
  };

  const validateTransportType = (
    transportType: TransportType | CreateTransportType
  ) => {
    if (
      operatorType === 'Hospitality' &&
      (transportType as TransportType).id === undefined
    )
      throw 'ID is required when updating a Hospitality Transport Type.';
    if (operatorType === 'Residential') {
      if (transportType.ingressHD === undefined)
        throw 'ingressHD is required when updating a Residential Transport Type.';
      if (transportType.ingressSD === undefined)
        throw 'ingressSD is required when updating a Residential Transport Type.';
      if (transportType.wanHD === undefined)
        throw 'wanHD is required when updating a Residential Transport Type.';
      if (transportType.wanSD === undefined)
        throw 'wanSD is required when updating a Residential Transport Type.';
    }
  };

  return {
    addTransportType,
    bulkTransportTypes,
    bulkAddTransportTypes,
    clearBulkAdds,
    getTransportType,
    getTransportTypes,
    updateTransportType,
    loading,
    transportType,
    transportTypes,
  };
};

const mapApiTransportType = (
  transportType: TransportType,
  operatorType: OperatorType
): ApiHosTransportType | ApiResTransportType => {
  if (operatorType === 'Residential')
    return mapApiResTransportType(transportType);
  return mapApiHosTransportType(transportType);
};

const mapApiHosTransportType = (
  transportType: TransportType
): ApiHosTransportType => ({
  ID: transportType.id as number,
  Active: transportType.active,
  Name: transportType.name,
});

const mapApiResTransportType = (
  transportType: TransportType
): ApiResTransportType => ({
  Active: transportType.active,
  Name: transportType.name,
  IngressHD: transportType.ingressHD ?? 0,
  IngressSD: transportType.ingressSD ?? 0,
  WanHD: transportType.wanHD ?? 0,
  WanSD: transportType.wanSD ?? 0,
});

const mapBulkResponse = (
  response: ApiBulkResponse<ApiResTransportType, ApiCreateTransportType>,
  operatorType: OperatorType
): BulkTransportTypes => ({
  success: response.Success.map((transportType) =>
    mapTransportType(transportType, operatorType)
  ),
  fail: response.Fail.map((transportType) => ({
    active: transportType.Active,
    name: transportType.Name,
    ingressHD: transportType.IngressHD,
    ingressSD: transportType.IngressSD,
    wanHD: transportType.WanHD,
    wanSD: transportType.WanSD,
  })),
});

export const mapTransportType = (
  transportType: ApiHosTransportType | ApiResTransportType,
  operatorType: OperatorType
): TransportType => {
  if (operatorType === 'Residential')
    return mapResTransportType(transportType as ApiResTransportType);
  else return mapHosTransportType(transportType as ApiHosTransportType);
};

export const mapHosTransportType = (
  transportType: ApiHosTransportType
): TransportType => ({
  id: transportType.ID,
  active: transportType.Active,
  name: transportType.Name,
});

export const mapResTransportType = (
  transportType: ApiResTransportType
): TransportType => ({
  id: randomNumber(10000, 90000),
  active: transportType.Active,
  name: transportType.Name,
  ingressHD: transportType.IngressHD,
  ingressSD: transportType.IngressSD,
  wanHD: transportType.WanHD,
  wanSD: transportType.WanSD,
});
